// src/pages/DeleteRenewalRecord.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Renew.css";
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";

export default function DeleteRenewalRecord() {
  const navigate = useNavigate();
  useEscapeToGoBack();

  const mmyyRef = useRef(null);
  const tailRef = useRef(null);

  const [mmyy, setMmyy] = useState("");
  const [receiptTail, setReceiptTail] = useState("");
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm1, setConfirm1] = useState(null);

  const onlyDigits = (s = "") => String(s).replace(/\D/g, "");
  const pad4 = (s = "") => onlyDigits(s).padStart(4, "0");
  const detectSeries = (tailDigits) => (tailDigits.length === 5 ? "S" : "B");

  const canonicalNewReceipt = () => {
    const mmYY = pad4(mmyy);
    const tail = onlyDigits(receiptTail);
    const series = detectSeries(tail);
    const tailNorm = series === "B" ? tail.padStart(4, "0") : tail;
    return `${series}/${mmYY}/${tailNorm}`;
  };

  const fmtDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    return `${dd}/${mm}/${yy}`;
  };

  useEffect(() => {
    mmyyRef.current?.focus();
  }, []);

  const onMmyyKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); tailRef.current?.focus(); }
  };
  const onTailKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); handlePreview(); }
  };

  const handlePreview = async () => {
    setErr(""); setPreview(null); setConfirm1(null);
    if (!mmyy || !receiptTail) {
      setErr("Please fill MMYY and Receipt Number.");
      return;
    }
    try {
      setBusy(true);
      // Reuse your existing preview endpoint (by NEW)
      const res = await axios.get("/api/renewals/payments/lookup-cancel-renew/by-new", {
        params: { mmyy: pad4(mmyy), receiptNoTail: onlyDigits(receiptTail) },
        // withCredentials: true, // if you rely on cookie auth
      });
      setPreview(res.data);
    } catch (e) {
      setErr(e?.response?.data?.message || "New receipt not found.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    const newReceipt = (preview?.newReceiptNo || canonicalNewReceipt()).trim();
    if (!window.confirm(`Delete renewal payment record for ${newReceipt}?\n\nThis DOES NOT change any tickets.`)) {
      return;
    }
    try {
      setBusy(true);
      const res = await axios.delete("/api/renewals/payments/delete-record/by-new", {
        params: { newReceiptNo: newReceipt },
        // withCredentials: true,
      });
      alert(`Deleted renewal record for ${newReceipt}.`);
      navigate("/dashboard");
    } catch (e) {
      alert(e?.response?.data?.message || "Deletion failed.");
      setBusy(false);
    }
  };

  return (
    <div className="renew-container">
      <h2 className="title">Delete Renewal Record (by New Receipt)</h2>

      <div className="form-group">
        <label>Enter Month and Year (MMYY):</label>
        <input
          ref={mmyyRef}
          value={mmyy}
          onChange={(e) => setMmyy(onlyDigits(e.target.value))}
          onKeyDown={onMmyyKeyDown}
          placeholder="MMYY"
          inputMode="numeric"
          pattern="\d{4}"
          maxLength={4}
          autoComplete="off"
        />
      </div>

      <div className="form-group">
        <label>Enter Receipt Number:</label>
        <input
          ref={tailRef}
          value={receiptTail}
          onChange={(e) => setReceiptTail(onlyDigits(e.target.value))}
          onKeyDown={onTailKeyDown}
          placeholder="0001 or 80001"
          inputMode="numeric"
          pattern="\d{4,5}"
          maxLength={5}
          autoComplete="off"
        />
      </div>

      <div style={{display:"flex", gap:8}}>
        <button className="search-btn" type="button" onClick={handlePreview} disabled={busy}>
          Preview
        </button>
        <button className="next-btn" type="button" onClick={handleDelete} disabled={busy}>
          Delete Record
        </button>
      </div>

      {err && <p className="error-message">{err}</p>}

      {preview && (
        <div className="transaction-details">
          <div className="details-grid">
            <p><strong>Old Receipt:</strong> {preview.receiptNo}</p>
            <p><strong>New Receipt:</strong> {preview.newReceiptNo || "-"}</p>
            <p><strong>Payment Amount:</strong> ${Number(preview.paymentAmount).toFixed(2)}</p>
            <p><strong>Paid On:</strong> {fmtDate(preview.paymentDateTime)}</p>
            <p><strong>Source:</strong> {preview.renewalSource || "-"}</p>
          </div>
          <div className="alert-box">
            <p><strong>Note:</strong> This will only delete the renewal payment record. It will NOT reactivate or delete any tickets.</p>
          </div>
        </div>
      )}
    </div>
  );
}
